import type { Ceviri } from "../tip";
import type { iceAktar as tr } from "../tr/iceAktar";

const s = (n: number, tekil: string, cogul: string): string => `${n} ${n === 1 ? tekil : cogul}`;

/** English dictionary · iceAktar. */
export const iceAktar: Ceviri<typeof tr> = {
  ekran: {
    baslik: "Import",
    aciklama: "Bring in accounts and transactions from another app with a CSV file.",
    hesapTuru: {
      araci_kurum: "Brokerage",
      banka: "Bank",
      cuzdan: "Wallet",
      borsa: "Crypto exchange",
      fiziki: "Physical",
    },
    durum: {
      yeni: "to be added",
      guncellenecek: "to be updated",
      ayni: "already identical",
      silinmis: "deleted in Parafon — not restored",
    },
    merkezYokKatalog: "Server not connected — the catalogue could not be queried.",
    katalogdaYokDisArama:
      "Not in the catalogue and the external search could not be run right now (this does not mean not found); try again later or value it manually.",
    katalogdaBulunamadi: "Not found in the catalogue.",
    aramaYapilamadi: (hata: string) => `Search could not be run: ${hata}`,
    merkezYokTefas: "Server not connected — the TEFAS price could not be fetched.",
    tefasSorulamadiHata: (hata: string) => `TEFAS price could not be queried: ${hata} — try again later.`,
    varlikEslestirilmedi: "Asset not matched",
    elleTefasYok: "A manually valued asset has no TEFAS archive; write the price into the file.",
    tefasSorulamadi: "TEFAS price could not be queried.",
    kilitKurumEkstresi: "Importing from a broker statement",
    kilitDosya: "Importing from a file",
    pdfMetinYok: (ad: string) =>
      `${ad}: no readable text on the pages (may be a scanned image); such statements cannot be read.`,
    ekstreTaninmadi: (sebep: string) =>
      `${sebep} If your broker's statement is not read yet, you can share its layout with the card below.`,
    karisikDosya: "Choose either one CSV file or one or more PDF statements (the TRY and foreign currency statements together).",
    sorunSutunu: "Problem",

    dosya: {
      baslik: "1 · File",
      aciklama:
        "One row is one transaction. Download the template for the columns and an example. \"Save as CSV\" from Excel is enough. Uploading the same file a second time duplicates nothing.",
      sec: "Choose file",
      secEtiket: "File to import",
      sablonIndir: "Download template",
      ekstreOkunamadi: "Statement could not be read",
      parola: "Statement password",
      parolaIpucu: "Only to open the file; not stored.",
      parolaTutmadi: "Wrong password.",
      ac: "Open",
      tanindi: (ad: string) => `${ad} recognised`,
      midasAciklama: (belgeler: string, atlanan: number) =>
        `${belgeler}. All are written to a single account; the TRY and foreign currency halves of each currency exchange are matched by their time.` +
        (atlanan > 0 ? ` ${s(atlanan, "unfilled order or operation", "unfilled orders or operations")} skipped.` : ""),
      sayiBicimi: "Number format",
      bicimAnlasilamadi: "Could not be detected from the file; choose one.",
      bicimOnerildi: "Suggested from the file; change it if wrong.",
      bicimTr: "Turkish — 1.234,56",
      bicimEn: "English — 1,234.56",
      sutunEksik: "Required column missing",
      sutunEksikAciklama: (eksik: string, baslik: string) =>
        `Not found: ${eksik}. Names in the header row: ${baslik}.`,
      oncesiTur: (paraBirimi: string) =>
        `The type is read from the sign of the quantity (+ buy, − sell). Currency from the header: ${paraBirimi}.`,
      oncesiTefas: " Fund codes without a prefix are searched as TEFAS funds.",
      oncesiDengeleme: " Portfolio switch / rebalancing rows marked \"Evet\" are written to the note.",
      oncesiYokSayilan: (liste: string) =>
        ` Ignored: ${liste} — the exchange rate comes from the server's CBRT archive.`,
      windows1254: " The file was read with the Turkish Windows encoding.",
      pdfTablo: " The table was read from the PDF; cells wrapping onto the next line were merged.",
      bilinmeyenSutunlar: (liste: string) => `Unrecognised columns were ignored: ${liste}.`,
    },

    hesaplar: {
      baslik: "2 · Accounts",
      satirYok: "No rows read.",
      grup: (ad: string) => `Account: ${ad}`,
      kimlik: (k: string) => `ID ${k}`,
      buHesap: "This account",
      secin: "Choose",
      yeniEkle: "Add as a new account",
      varOlan: (ad: string, devreDisi: boolean) => `Existing: ${ad}${devreDisi ? " (disabled)" : ""}`,
      devreDisiBaslik: "This account is disabled",
      devreDisiAciklama:
        "It can be loaded: the transactions are written to the vault, but until the account is reopened it is not shown outside the Accounts page and is not included in totals. You can reopen it with \"Reopen\" on the Accounts page.",
      birdenCok: "There is more than one account at this broker; choose which one to load into.",
      hesapAdi: "Account name",
      araciKurum: (ad: string) => `Broker: ${ad}`,
      adBos: "Name cannot be empty.",
      tur: "Type",
      anaParaBirimi: "Main currency",
    },

    varliklar: {
      baslik: "3 · Assets",
      grup: (ad: string) => `Asset: ${ad}`,
      araniyor: "Searching the catalogue…",
      birdenCok: "More than one match; choose one.",
      elleEkle: "Add as a manually valued asset",
      secmezseniz: "If you do not choose, this asset's rows are not added and fall into the problem rows.",
      elle: "Manually valued asset — its price is entered on the Manual price screen.",
      yok: "No assets.",
    },

    onizleme: {
      baslik: "4 · Preview",
      okundu: (n: number, sorunlu: number) =>
        `${s(n, "row", "rows")} read${sorunlu > 0 ? `, ${s(sorunlu, "row has", "rows have")} problems (not added)` : ""}.`,
      geriYatirim: (n: number, desenden: number) =>
        `${s(n, "row", "rows")} read as reinvested dividends` +
        (desenden > 0 ? ` (${desenden} from the "buy + negative fee + dividend" pattern)` : "") +
        ": each is added as a dividend income and a buy of the same amount.",
      arsivden: (n: number) =>
        `The unit price of ${s(n, "row", "rows")} was taken from that day's TEFAS price (stated in the transaction note).`,
      bekleniyor: (n: number) =>
        `The TEFAS price of ${s(n, "row", "rows")} could not be fetched yet (the fund history may still be downloading on the server).`,
      yenidenSor: "Query TEFAS prices again",
      hazirDegil: "The preview is prepared once the account and asset choices are complete.",
      ozetEtiket: "Comparison with the portfolio summary",
      ozetBaslik: (tarih: string) => `Statement portfolio summary (${tarih}) against the vault, including the items to import:`,
      ozetTutuyor: (n: number) => `quantity matches for ${s(n, "asset", "assets")}.`,
      ozetTutmuyor: (n: number) => `quantity does not match for ${s(n, "asset", "assets")}.`,
      uyari: "Warning",
      ekstrede: "statement",
      kasada: "vault",
      eslesmedi: "asset not matched",
      oncekiDonemler: "If the statement does not start at the account's opening, import the statements of earlier periods too.",
      sayilarEtiket: "Preview counts",
      hesapSayisi: (n: number) => s(n, "account", "accounts"),
      ve: " and ",
      hareketSayisi: (n: number) => s(n, "transaction", "transactions"),
      sorunlu: "Problem rows",
      satir: (n: number) => `Row ${n}:`,
      sorunluIndir: "Download problem rows",
      csvDosyasi: "CSV file",
      kilitAciklama: "The file was read and previewed; no records were written.",
      kilitYeniHesap: "Adding the new accounts in this file",
      kilitHesapAciklama: (etkin: number, yeni: number, sinir: number) =>
        `The vault has ${s(etkin, "active account", "active accounts")}, the file adds ${s(yeni, "new account", "new accounts")}; the limit is ${sinir}.`,
      yaziliyor: "Writing…",
      yazilacakYok: "Nothing to write",
      iceAktar: (n: number) => `Import (${s(n, "record", "records")})`,
      tamamBaslik: "Import complete",
      tamam: (hesap: number, hareket: number, guncel: number) =>
        `${s(hesap, "account", "accounts")} added, ${s(hareket, "transaction", "transactions")} added, ${s(guncel, "record", "records")} updated.`,
      izlenemeyen: (liste: string) => `Could not be added to price tracking (later from the Tracking screen): ${liste}.`,
    },
  },

  rapor: {
    tur: {
      accounts: "Account",
      transactions: "Transaction",
      manualValuations: "Manual valuation",
      manualPrices: "Manual price",
      settings: "Setting",
    },
    bos: "This file has no records — an empty backup. The records in your vault were not touched.",
    hepsiAyni: "Every record in the file was already identical in your vault; nothing changed.",
    hicYazilmadi: "No records were written — details below.",
    dosyada: "in file",
    yazildi: "written",
    zatenAyni: "identical",
    atlandi: "skipped",
    atlananlar: (n: string) => `${n} ${n === "1" ? "record" : "records"} skipped — reasons`,
    hicAtlanmadi: "No records were skipped.",
  },

  maske: {
    yok: "None.",
    kapat: "Hide",
    ac: "Show",
    tumunuGoster: "Show all",
    ozet: (sayfa: number, parca: number) =>
      `${s(sayfa, "page", "pages")}, ${s(parca, "text piece", "text pieces")}. The preview below is exactly the file to be downloaded.`,
    sayfa: "Page",
    onizleme: "Masked preview",
    kelimeIpucu: "If a word you do not want shown is visible, tap it to hide it. You can show a hidden column name.",
    acikKelimeler: "Visible words",
    kapaliKelimeler: "Hidden words",
    indir: "Download masked file",
    baslik: "Broker statement · share its layout",
    aciklama:
      "Broker statements (PDF) are not read directly yet; the first broker is Midas. The reader only needs the statement's layout, not its contents. Digits become 9, names and codes become X; only header words such as \"Tarih\" or \"Adet\" stay visible. The PDF is processed on this device and not stored; you download and share the masked file yourself, or approve the text you see and send it to development.",
    pdfSec: "Choose PDF",
    pdfEtiket: "Statement to mask (PDF)",
    temizle: "Clear",
    okunuyor: "Reading PDF",
    parola: "PDF password",
    parolaIpucu: "Only to open this file; not stored.",
    parolaTutmadi: "Wrong password.",
    metinYokBaslik: "This PDF has no text",
    metinYok: (n: number) =>
      `No readable text was found on any of the ${s(n, "page", "pages")}; the pages may have been scanned as images. Such statements cannot be read. Try the PDF or Excel downloaded from the broker's website.`,
    okunamadi: "PDF could not be read",
  },

  geriBildirim: {
    merkezYok: "The server is not connected in this session; cannot send.",
    ac: "Send to development…",
    grup: "Send to development",
    aciklama: (gun: number) =>
      `The text below goes to the Parafon server only if you press "Send", and is reviewed in the next development round. Nothing about who sent it, the device, the vault or the account is sent; digits become 9 and unrecognised names become X. A processed record is deleted; if not processed, it is deleted automatically after ${s(gun, "day", "days")}.`,
    metin: "Text to be sent",
    gonderilemezBaslik: "This text cannot be sent",
    sinirAsiyor: "The text exceeds the server's limit; download the file and share it.",
    sebep: (sebep: string) => `Reason: ${sebep}.`,
    gonderildi: "Sent",
    basvuruKodu: "Reference code:",
    buKodla: ". You can ask about it with this code.",
    gonderiliyor: "Sending…",
    gonder: "Send",
    vazgec: "Cancel",
    gonderilemedi: "Could not be sent",
    gonderilemez: (sebep: string) => `Cannot be sent: ${sebep}.`,
    cokGonderim: "The server received too many submissions from this connection in a short time; try again in an hour.",
    dolu: "The server's limit of records awaiting review is full; try again later.",
    yanitAnlasilamadi: "The server's response could not be understood.",
    metinBaslik: "Parafon · rows that could not be imported",
    metinBicim: (ad: string) => `Format: ${ad}`,
    metinKural: "Rule: digit → 9; letter not in the dictionary → X/x",
    metinSorun: "Problem:",
    satirSiniri: (n: number) => `Only the first ${s(n, "row", "rows")} are sent.`,
    kelimeIpucu:
      "If a word you do not want shown is visible, tap it to hide it; if a word needed for recognition (e.g. the transaction type) is hidden, you can show it.",
  },

  okuma: {
    alan: {
      miktar: "Quantity",
      fiyat: "Price",
      tutar: "Amount",
      masraf: "Fee",
      vergi: "Tax",
      alinanTutar: "Received amount",
    },
    sayiOkunamadi: (alan: string, ham: string, ornek: string) =>
      `${alan} could not be read: "${ham}" (number format ${ornek})`,
    haneTasiyor: (alan: string, hane: number, deger: string, sinir: number) =>
      `${alan} has ${hane} decimal places (${deger}); Parafon stores at most ${sinir}, it does not round`,
    tarihOkunamadi: (ham: string) => `Date could not be read: "${ham}" (DD.MM.YYYY or YYYY-MM-DD)`,
    valorOkunamadi: (ham: string) => `Value date could not be read: "${ham}"`,
    valorOnce: "The value date cannot be before the trade date",
    hesapBos: "Account is empty",
    turAnlasilmadi: (ham: string) => `Type not understood: "${ham}"`,
    paraBirimiYok: (ham: string, liste: string) => `Currency not in the list: "${ham}" (${liste})`,
    masrafEksiTemettusuz: "Fee cannot be negative (without \"temettu\" in the description it is not a reinvestment)",
    eksiMasrafTutmuyor: (masraf: string, alis: string) =>
      `Negative fee (${masraf}) does not equal the buy amount (quantity × price = ${alis}) — could not be read as a reinvested dividend`,
    masrafEksi: "Fee cannot be negative",
    temettuBelirsiz:
      "The description says \"temettu\" (dividend) but there is no negative fee (dividend amount) — it is unclear whether this is a reinvested dividend or a buy. " +
      "Fix: if a dividend, write the negative dividend amount as the fee; if a buy, remove \"temettu\" from the description",
    vergiEksi: "Tax cannot be negative",
    alinanTutarGerekli: "Received amount required (currency exchange)",
    alinanTutarSifir: "Received amount must be greater than zero",
    alinanParaBirimiYok: (ham: string, liste: string) => `Received currency not in the list: "${ham}" (${liste})`,
    alinanParaBirimiAyni: "Received currency must differ from the given one",
    alinanYalnizDoviz: "Received amount and currency are only allowed in a currency exchange",
    tutarGerekli: "Amount required (cash transaction)",
    tutarSifir: "Amount must be greater than zero",
    temettudeVarlik: "A dividend requires an asset",
    varlikGerekli: "Asset required",
    miktarGerekli: "Quantity required",
    miktarSifir: "Quantity must be greater than zero",
    fiyatGerekli: "Price required",
    fiyatEksi: "Price cannot be negative",
    carpimSigmiyor: "Quantity × price does not fit in 12 decimal places",
    kimlikTekrar: (kimlik: string) => `Source ID appears more than once in the file: ${kimlik}`,
  },

  midas: {
    ad: "Midas account statement",
    sutunSayisi: (bulunan: number, beklenen: number, metin: string) =>
      `${bulunan} columns found, ${beklenen} expected (${metin})`,
    sutunBeklenmedik: (no: number, metin: string) => `Column ${no} unexpected: "${metin}"`,
    transferVar: "The share transfer section has rows; this section is not read yet.",
    baslikTaninmadi: (belge: string, bolum: string, sebep: string) =>
      `${belge}: ${bolum} header not recognised — ${sebep}`,
    emirDurumu: (durum: string) => `Order status not recognised: "${durum}" (there is a filled price)`,
    satirOkunamadi: "Row could not be read (date, side, quantity, price or amount)",
    adetFiyatTutar: "Quantity, price or amount could not be read",
    tutarTutmuyor: (carpim: string, tutar: string) =>
      `Quantity × average price (${carpim}) does not match the trade amount (${tutar})`,
    birimFiyat: "Unit price could not be calculated",
    hesapIslemiOkunamadi: "Account operation could not be read (date or amount)",
    hesapIslemiTaninmadi: (tip: string, aciklama: string | null) =>
      `Account operation not recognised: "${tip}"${aciklama === null ? "" : ` (${aciklama})`}`,
    temettuOkunamadi: "Dividend row could not be read",
    temettuTutmuyor: (brut: string, stopaj: string, net: string) =>
      `Gross − withholding tax (${brut} − ${stopaj}) does not match the net dividend (${net})`,
    dosyaYok: "No file.",
    taninmayan: (adlar: string) => `${adlar}: not a recognised broker statement.`,
    dovizYarisiYok: (alis: boolean, belge: string) =>
      `The other half of the currency ${alis ? "purchase" : "sale"} is missing (${belge}): choose the TRY and foreign currency statements together`,
    dovizCoklu: "More than one currency exchange at the same time: could not be matched",
    kurFarkli: (a: string, b: string) => `The exchange rate differs between the two statements (${a} / ${b})`,
  },

  oncesi: {
    ad: "Pre-Parafon records file",
    dengelemeAnlasilmadi: (ham: string) =>
      `Portfolio switch / rebalancing not understood: "${ham}" (Evet / Hayir)`,
    arsivYok: "The fund's price history is not on the server yet; it may be downloading — try again later.",
    ufukOncesi: (gun: string, baslangic: string) =>
      `${gun}: the server's price history starts on ${baslangic}; no price can be fetched for this day.`,
    indirilmedi: (gun: string, from: string, to: string) =>
      `${gun}: the server has not downloaded this day yet (available ${from} – ${to}); try again later.`,
    bosluk: (gun: string) => `${gun}: this day is missing at the source (known gap); write the price into the file.`,
    yayimlanmamis: (gun: string) =>
      `${gun}: TEFAS did not publish a price on this day (may be a weekend or holiday); check the date or the price.`,
  },

  not: {
    nemaGeliri: "interest income",
    stopaj: "withholding tax",
    odemeTarihi: "payment date",
    kismenAdet: (adet: string, gerceklesen: string) => `partially filled (order quantity ${adet}, filled ${gerceklesen}; rest cancelled)`,
    kismenTutar: (tutar: string, gerceklesen: string) => `partially filled (order amount ${tutar}, filled ${gerceklesen}; rest cancelled)`,
    birimFiyat: (ortalama: string) => `unit price = trade amount ÷ quantity (statement average ${ortalama})`,
    kur: (kur: string) => `rate ${kur}`,
    dengeleme: "Portfolio switch / rebalancing",
    arsivFiyati: (gun: string) => `Unit price from the TEFAS archive (${gun})`,
  },

  pdfGecersiz: "The file is not a valid PDF.",
  dosyaOkunamadi: "The file could not be read.",
};
